import React, { useState, useMemo } from 'react';
import DogCard from './DogCard';

const AGE_GROUPS = [
    { key: 'puppy', label: 'Cachorro', desc: '≤1yr', test: (age) => age !== null && age <= 1 },
    { key: 'young', label: 'Joven', desc: '1-3yrs', test: (age) => age !== null && age > 1 && age <= 3 },
    { key: 'adult', label: 'Adulto', desc: '3-7yrs', test: (age) => age !== null && age > 3 && age <= 7 },
    { key: 'senior', label: 'Senior', desc: '7+yrs', test: (age) => age !== null && age > 7 },
];

const SIZE_GROUPS = [
    { key: 'xs', label: 'XS', desc: '0–5kg', test: (w) => w > 0 && w <= 5 },
    { key: 's', label: 'S', desc: '5.1–10.9kg', test: (w) => w > 5 && w <= 10.9 },
    { key: 'm', label: 'M', desc: '11–20kg', test: (w) => w > 10.9 && w <= 20 },
    { key: 'l', label: 'L', desc: '21–35kg', test: (w) => w > 20 && w <= 35 },
    { key: 'xl', label: 'XL', desc: '35+kg', test: (w) => w > 35 },
];

const SORT_OPTIONS = [
    { key: 'name-asc', label: 'Nombre A→Z' },
    { key: 'name-desc', label: 'Nombre Z→A' },
    { key: 'age-asc', label: 'Edad (más joven)' },
    { key: 'age-desc', label: 'Edad (más mayor)' },
    { key: 'weight-asc', label: 'Peso (menor)' },
    { key: 'weight-desc', label: 'Peso (mayor)' },
    { key: 'recent', label: 'Recién agregados' },
];

const DogsDisplay = ({ 
    dogs, 
    loading, 
    onEdit, 
    onDelete, 
    onViewDetails 
}) => {
    const [showFilters, setShowFilters] = useState(false);
    const [ageFilter, setAgeFilter] = useState('');
    const [sizeFilter, setSizeFilter] = useState('');
    const [breedFilter, setBreedFilter] = useState('');
    const [sortBy, setSortBy] = useState('name-asc');

    const getEffectiveAge = (dog) => {
        if (dog.calculatedAge && typeof dog.calculatedAge.years === 'number') {
            return dog.calculatedAge.years;
        }
        if (dog.dateOfBirth) {
            const now = new Date();
            const dob = new Date(dog.dateOfBirth);
            if (!isNaN(dob.getTime()) && dob <= now) {
                let years = now.getFullYear() - dob.getFullYear();
                const monthDiff = now.getMonth() - dob.getMonth();
                const dayDiff = now.getDate() - dob.getDate();
                if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) years--;
                return years;
            }
        }
        return dog.age || null;
    };

    const uniqueBreeds = useMemo(() => 
        [...new Set(dogs.map(d => d.breed).filter(Boolean))].sort(),
        [dogs]
    );

    const hasActiveFilters = ageFilter || sizeFilter || breedFilter;

    const filteredAndSorted = useMemo(() => {
        let result = [...dogs];

        // Age filter
        if (ageFilter) {
            const group = AGE_GROUPS.find(g => g.key === ageFilter);
            if (group) result = result.filter(d => group.test(getEffectiveAge(d)));
        }

        // Size filter
        if (sizeFilter) {
            const group = SIZE_GROUPS.find(g => g.key === sizeFilter);
            if (group) result = result.filter(d => d.weight && group.test(d.weight));
        }

        // Breed filter
        if (breedFilter) {
            result = result.filter(d => d.breed === breedFilter);
        }

        // Sort
        result.sort((a, b) => {
            switch (sortBy) {
                case 'name-asc': return (a.name || '').localeCompare(b.name || '');
                case 'name-desc': return (b.name || '').localeCompare(a.name || '');
                case 'age-asc': return (getEffectiveAge(a) || 0) - (getEffectiveAge(b) || 0);
                case 'age-desc': return (getEffectiveAge(b) || 0) - (getEffectiveAge(a) || 0);
                case 'weight-asc': return (a.weight || 0) - (b.weight || 0);
                case 'weight-desc': return (b.weight || 0) - (a.weight || 0);
                case 'recent': return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                default: return (a.name || '').localeCompare(b.name || '');
            }
        });

        return result;
    }, [dogs, ageFilter, sizeFilter, breedFilter, sortBy]);

    const clearFilters = () => {
        setAgeFilter('');
        setSizeFilter('');
        setBreedFilter('');
    };

    if (loading) {
        return <div className="loading">Cargando perros...</div>;
    }

    if (dogs.length === 0) {
        return (
            <div className="no-dogs">
                <p>No hay perros registrados en el sistema.</p>
                <p>🐕 ¡Agrega tu primer amigo peludo!</p>
            </div>
        );
    }

    return (
        <div className="dogs-display">
            {/* Toolbar */}
            <div className="dogs-toolbar">
                <div className="toolbar-left">
                    <span className="dogs-count">{filteredAndSorted.length} de {dogs.length} perros</span>
                </div>
                <div className="toolbar-right">
                    <button
                        className={`filter-toggle-btn ${showFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                        onClick={() => setShowFilters(prev => !prev)}
                    >
                        🔍 Filtrar {hasActiveFilters && <span className="filter-dot"></span>}
                    </button>
                    <select
                        className="sort-select"
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                    >
                        {SORT_OPTIONS.map(opt => (
                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <div className="dogs-filter-panel">
                    <div className="filter-section">
                        <label>Edad</label>
                        <div className="filter-chips">
                            {AGE_GROUPS.map(g => (
                                <button
                                    key={g.key}
                                    className={`filter-chip ${ageFilter === g.key ? 'active' : ''}`}
                                    onClick={() => setAgeFilter(ageFilter === g.key ? '' : g.key)}
                                >
                                    {g.label} <span className="chip-desc">{g.desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="filter-section">
                        <label>Tamaño</label>
                        <div className="filter-chips">
                            {SIZE_GROUPS.map(g => (
                                <button
                                    key={g.key}
                                    className={`filter-chip ${sizeFilter === g.key ? 'active' : ''}`}
                                    onClick={() => setSizeFilter(sizeFilter === g.key ? '' : g.key)}
                                >
                                    {g.label} <span className="chip-desc">{g.desc}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="filter-section">
                        <label>Raza</label>
                        <select
                            className="breed-filter-select"
                            value={breedFilter}
                            onChange={e => setBreedFilter(e.target.value)}
                        >
                            <option value="">Todas las razas</option>
                            {uniqueBreeds.map(b => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>
                    </div>
                    {hasActiveFilters && (
                        <button className="clear-all-filters-btn" onClick={clearFilters}>
                            ✕ Limpiar filtros
                        </button>
                    )}
                </div>
            )}

            {/* Dog List */}
            {filteredAndSorted.length === 0 ? (
                <div className="no-dogs">
                    <p>No hay perros que coincidan con los filtros seleccionados.</p>
                </div>
            ) : (
                <div className="dogs-grid">
                    {filteredAndSorted.map(dog => (
                        <DogCard
                            key={dog._id}
                            dog={dog}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onViewDetails={onViewDetails}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default DogsDisplay;
